/**
 * Dashboard API — fetches all dashboard analytics data.
 *
 * Endpoint:
 *   GET /bot/api/dashboard?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 *
 * Returns: { heroMetrics, volumeData, geographicData, recentConversations,
 *            topTopics, escalatedTopics, channelData, languageData }
 */

import { getBaseUrl } from './utils';
import { getAuthHeaders } from './auth';

const DASHBOARD_BASE = () => `${getBaseUrl()}/bot/api/dashboard`;

/**
 * Fetch all dashboard data for a given date range.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {object} [opts] - Optional overrides
 * @param {number} [opts.topTopicsLimit=5]
 * @param {number} [opts.escalatedLimit=3]
 * @returns {Promise<object>} Dashboard data
 */
export async function getDashboardData(startDate, endDate, opts = {}) {
  const params = new URLSearchParams();

  if (startDate) {
    params.set('start_date', _formatDate(startDate));
  }
  if (endDate) {
    params.set('end_date', _formatDate(endDate));
  }
  if (opts.topTopicsLimit) {
    params.set('top_topics_limit', String(opts.topTopicsLimit));
  }
  if (opts.escalatedLimit) {
    params.set('escalated_limit', String(opts.escalatedLimit));
  }

  const url = `${DASHBOARD_BASE()}?${params.toString()}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail || data?.message || `Dashboard request failed (${res.status})`);
  }

  return res.json();
}

/**
 * Format a Date object as YYYY-MM-DD.
 */
function _formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
